import React, { useState, useEffect, useCallback } from 'react';
import ModalWindow from '../modalWindow/ModalWindow';
import './PhotoPicker.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PhotoPicker = ({ 
  isOpen, 
  onClose, 
  onSelect,
  position,
  currentPhoto = null,
  onDragEnd,
  zIndex,
  onFocus,
  templateId
}) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(12); // Adjust based on grid layout
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeletingUrl, setIsDeletingUrl] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchPhotos(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentPage]);

  const validateImage = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Please select a valid image file (PNG, JPEG, GIF, or WebP)');
    }
    
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('Image size should be less than 10MB');
    }
  };

  const uploadImage = async (file) => {
    try {
      validateImage(file);
      
      const formData = new FormData();
      formData.append('image', file);
      
      const token = sessionStorage.getItem('token');
      
      // Create XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      
      // Setup promise to handle the upload
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            setUploadProgress(progress);
          }
        };
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error('Upload failed'));
          }
        };
        
        xhr.onerror = () => reject(new Error('Upload failed'));
      });
      
      // Configure and send request
      xhr.open('POST', `${API_URL}/api/event-invites/template/${templateId}/upload-image`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
      
      const result = await uploadPromise;
      return result;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    // Ensure we're handling a file drop
    if (!e.dataTransfer?.types.includes('Files')) {
      return;
    }
    
    const files = e.dataTransfer.files;
    if (!files?.length) return;
    
    const file = files[0]; // Handle only the first file
    
    try {
      setIsUploading(true);
      setError(null);
      setUploadProgress(0);
      
      await uploadImage(file);
      
      // Refresh the photo list
      await fetchPhotos(currentPage);
      
    } catch (error) {
      setError(error.message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, templateId]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the grid area
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const deleteImage = async (photoUrl) => {
    try {
      const token = sessionStorage.getItem('token');
      const imageId = photoUrl.split('/').pop().split('.')[0]; // Extract ID from URL
      
      const response = await fetch(`${API_URL}/api/event-invites/template/${templateId}/image/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete image');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  };

  const handleDeleteClick = async (e, photoUrl) => {
    e.stopPropagation(); // Prevent triggering photo selection
    
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    let photoToDelete;
    try {
      setIsDeletingUrl(photoUrl);
      
      // Store the photo before removing it
      photoToDelete = photos.find(photo => photo.url === photoUrl);
      
      // Optimistically remove from UI
      setPhotos(prev => prev.filter(photo => photo.url !== photoUrl));
      
      await deleteImage(photoUrl);
      
      // Refresh the current page to get updated results
      await fetchPhotos(currentPage);
      
    } catch (error) {
      // Restore the photo if deletion fails
      if (photoToDelete) {
        setPhotos(prev => [...prev, photoToDelete]);
      }
      setError(error.message);
    } finally {
      setIsDeletingUrl(null);
    }
  };

  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setIsUploading(true);
      setError(null);
      setUploadProgress(0);
      
      await uploadImage(file);
      
      // Refresh the photo list
      await fetchPhotos(currentPage);
      
    } catch (error) {
      setError(error.message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset the input value to allow uploading the same file again
      e.target.value = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, templateId]);

  const fetchPhotos = async (page) => {
    try {
      setLoading(true);
      setError(null);

      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/event-invites/template/${templateId}/photos?page=${page}&count=${itemsPerPage}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error('Failed to fetch photos');
      }

      const data = await response.json();

      // Update pagination state
      if (data.pagination) {
        setTotalPages(data.pagination.totalPages);
        
        // If current page is out of bounds, reset to page 1
        if (page > data.pagination.totalPages && data.pagination.totalPages > 0) {
          setCurrentPage(1);
          return; // Will trigger another fetch with page 1
        }
      }

      // Transform URLs into photo objects
      const photoObjects = (data.photos || []).map(url => ({
        url,
        name: url.split('/').pop() // Get filename from URL
      }));
      setPhotos(photoObjects);
    } catch (err) {
      console.error('Error fetching photos:', err);
      setError(err.message);
      setPhotos([]);
      
      // Handle unauthorized error
      if (err.message === 'Unauthorized') {
        // Optionally redirect to login or handle unauthorized state
        console.log('User is unauthorized. Should redirect to login.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWindow
      isOpen={isOpen}
      onClose={onClose}
      title="Photo Gallery"
      position={position || { x: 0, y: 0 }}
      onDragEnd={onDragEnd}
      zIndex={zIndex}
      onFocus={onFocus}
      headerContent={
        <div className="header-nav-controls">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1 || loading}
            className="header-nav-button"
            title="Previous page"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span className="header-nav-count">{currentPage}/{totalPages}</span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages || loading}
            className="header-nav-button"
            title="Next page"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      }
    >
      <div className="photo-picker">
        {/* {loading && (
          // <div className="photo-picker-loading"></div>
        )} */}
        
        {error && (
          <div className="photo-picker-error">
            {error}
          </div>
        )}

        <div 
          className={`photo-picker-grid ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="file-upload"
            accept=".png,.jpg,.jpeg,.gif,.webp"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {isUploading && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div 
                  className="progress-bar-fill" 
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <div className="progress-text">Uploading... {Math.round(uploadProgress)}%</div>
            </div>
          )}
          {photos.map((photo, index) => (
            <button
              key={photo.url || index}
              className={`photo-picker-item ${
                currentPhoto === photo.url ? 'selected' : ''
              }`}
              onClick={() => {
                onSelect(photo.url, false); // false means don't close after selection
              }}
              title="Click to select this photo"
            >
              <div className="photo-picker-item-preview">
                <button
                  className="delete-button"
                  onClick={(e) => handleDeleteClick(e, photo.url)}
                  title="Delete image"
                >
                  ✕
                </button>
                {isDeletingUrl === photo.url && (
                  <div className="deleting-overlay">
                    <div className="deleting-spinner" />
                    <div className="deleting-text">Deleting...</div>
                  </div>
                )}
                <img 
                  src={photo.url} 
                  alt={photo.name || 'Photo preview'} 
                  className="photo-preview"
                />
              </div>
            </button>
          ))}
        </div>
      </div>
    </ModalWindow>
  );
};

export default PhotoPicker;